import { array, bool, func, node, object, string } from 'prop-types';
import React from 'react';
import Promise from 'Promise'
import buildAtlasLinks from './utils/buildAtlasLinks';
import {
  policies,
  productEnabled,
  products,
  learnURL,
  performURL,
  careerURL,
} from '@get-bridge/learn-legacy-shared/dist/utils/accountConfig';
import isFeatureEnabled from '@get-bridge/learn-legacy-shared/dist/utils/isFeatureEnabled';
import Atlas from '@get-bridge/atlas';
import NotificationData from './notifications/NotificationData';
import Actions from 'Actions';
import TaskData from './tasks/TaskData';
import UserData from './users/UserData';
import LinkInterceptor from './LinkInterceptor';
import Runtime from '@get-bridge/learn-legacy-shared/dist/core/Runtime';
import { toast, toastOfError } from '@get-bridge/learn-legacy-packages.bridge-toaster';
import { objCamelize } from '@get-bridge/learn-legacy-shared/dist/utils/object';
import ResponsiveLayout from '@get-bridge/learn-legacy-shared/dist/components/ResponsiveLayout';
import cx from 'classnames';
import tapestrySDK from '@get-bridge/tapestry-sdk';
import MobileAppLinkStore from './mobile_app_links/MobileAppLinkStore';
import AuthStore from '@get-bridge/learn-legacy-shared/dist/stores/AuthStore';
import CurrentUser from '@get-bridge/learn-legacy-shared/dist/entities/CurrentUser';

const toastRegularMsg = msg => toast(msg);
const toastErrorMsg = msg => toastOfError(msg);
const shouldShowPolicy = policy =>
  !!policy && policy.regionType !== 'none_selected';
const onSkipToContent = () => {
  if (tapestrySDK.isPlatformActive()) {
    tapestrySDK.focusContentNode();
  } else {
    // this is copy-pasted from tapestrySDK.focusContentNode(), see tapestry/src/index.js
    // once every piece of Learn is behind the runtime, we can get rid of this and just call focusContentNode() instead.

    /*
      Priority list for finding the "main content" to skip to:
        1. if there is a "main" aria landmark, and it contains an h1, focus the h1.
        2. if there is a main landmark, but no h1, focus the main landmark,
        3. if there is no main landmark, but there is an h1 anywhere on the page, focus the h1.
        4. otherwise, find the global nav content element and focus that.
    */
    const selectors = ['[role="main"] h1', '[role="main"]', 'h1', '.global-nav__content iframe'];
    const mainContent = selectors.reduce(
      (firstFoundNode, selector) => firstFoundNode || document.querySelector(selector),
      null
    );
    if (mainContent) {
      const prevTabIndex = mainContent.getAttribute('tabindex');
      if (prevTabIndex) {
        mainContent.focus();
      } else {
        // temporarily set the tabIndex to -1 so we can skip focus to the element,
        // then set it back to its original value
        mainContent.setAttribute('tabindex', '-1');
        mainContent.focus();
        mainContent.setAttribute('tabindex', prevTabIndex);
      }
    } else {
      console.warn(
        'Tried to Skip to Content, but did not find either a role=main landmark or an H1 anywhere in the DOM!'
      );
    }
  }
}

const formatLocales = locales =>
  Object.keys(locales).map(key => ({
    id: key.toString(),
    name: locales[key].locales[key],
  }));

const resourcePath = {
  admin_nav: 'admin/lti_resource',
  learner_nav: 'learner/lti_resource',
};

function formatLtiLink({
  id,
  placement,
  text: name,
  iconUrl,
  newTabLaunchUrl,
}) {
  const logo = ({ className }) => (
    <i className={className}>
      <img src={iconUrl} className="global-nav__resource-icon" />
    </i>
  );
  logo.propTypes = {
    className: string,
  };

  return {
    name,
    logo,
    href: newTabLaunchUrl || `//${learnURL()}/${resourcePath[placement]}/${id}`,
    placement,
    openInNewTab: !!newTabLaunchUrl,
  };
}

export default class AtlasWrapper extends React.Component {
  static propTypes = {
    accountInfo: object,
    children: node,
    location: object,
    currentUserInfo: object,
    profile: object,
    notifications: object,
    tasks: object,
    locales: object,
    teammates: object,
    manager: object,
    permissions: object,
    ltiPlacements: array,
    stopMasquerading: func,
    isMasquerading: bool,
    userRoles: array,
    brandingInfo: object,
    performSettings: object,
  };

  static defaultProps = {
    permissions: {}
  };

  getMobileAppLinks() {
    return new Promise(resolve => {
      const cachedLinks = MobileAppLinkStore.get();
      if (cachedLinks) {
        resolve(cachedLinks);
      } else {
        MobileAppLinkStore.fetch().then(resolve);
      }
    });
  }

  getCustomMasqueradingMessage = () => {
    if (
      !AuthStore.isMasqueradingInRootAccount() &&
      AuthStore.isMasqueradingInSubAccount()
    ) {
      const user = CurrentUser.__getUserFromSession__();
      return I18n.t('We just teleported you to %{subaccount}', {
        subaccount: user.subAccount.name,
      });
    }
    return undefined;
  }

  render() {
    const {
      accountInfo,
      currentUserInfo,
      notifications,
      tasks,
      profile,
      locales,
      teammates,
      manager,
      ltiPlacements,
      stopMasquerading,
      isMasquerading,
      location,
      brandingInfo,
      performSettings,
    } = this.props;

    const userInfo = currentUserInfo || {};
    if (!userInfo.hasOwnProperty('purchasedProducts')) {
      userInfo.purchasedProducts = objCamelize(products());
    }

    //add roles to the user object
    if (!userInfo.hasOwnProperty('roles')) {
      userInfo.roles = this.props.userRoles || [];
    }

    const learnHost =  learnURL();
    const performHost = performURL();
    const careerHost = careerURL();
    const hosts = {
      learn: learnHost && `//${learnHost}`,
      perform: performHost && `//${performHost}`,
      career: careerHost && `//${careerHost}`,
    };
    const { goalTerminology } = (performSettings || {});
    const links = buildAtlasLinks({
      hosts,
      user: userInfo,
      goalTerminology,
    });
    const locationObject = {
      pathname: location.pathname,
      search: location.search,
      origin: window.location.origin,
      protocol: window.location.protocol,
    };
    const { acceptableUsePolicy, privacyPolicy } = policies();
    const teammateData = UserData(currentUserInfo, manager, teammates);

    return (
      <LinkInterceptor>
        <Atlas
          accountInfo={accountInfo}
          brandingInfo={brandingInfo}
          currentUserInfo={userInfo}
          getMobileAppLinks={this.getMobileAppLinks}
          hosts={hosts}
          isFeatureEnabled={isFeatureEnabled}
          isMasqueraded={isMasquerading}
          languages={formatLocales(locales)}
          links={links}
          location={location.pathname}
          locationObject={locationObject}
          ltiLinks={ltiPlacements ? ltiPlacements.map(formatLtiLink) : []}
          masqueradeMessage={this.getCustomMasqueradingMessage()}
          notifications={NotificationData(notifications)}
          onError={toastErrorMsg}
          onLogout={Function.prototype} // SingleSPA handles logout logic, see bridge-logout app
          onMasqueradeStop={isMasquerading ? stopMasquerading : undefined}
          onPasswordChange={Actions.updatePassword}
          onProfileChange={this.handleProfileChange}
          onSkipToContent={onSkipToContent}
          onSuccess={toastRegularMsg}
          productEnabled={productEnabled}
          profile={profile || {}}
          runtime={Runtime}
          showAUPLink={shouldShowPolicy(acceptableUsePolicy)}
          showPPLink={shouldShowPolicy(privacyPolicy)}
          tasks={TaskData(userInfo, tasks, teammateData)}
          uploadAvatar={Actions.uploadFile}
          goalTerminology={goalTerminology}
        >
          {this.props.children && (
            <div className="global-nav__content">
              <ResponsiveLayout renderWithClassname={this.renderContent} />
            </div>
          )}
        </Atlas>
      </LinkInterceptor>
    );
  }

  renderContent = className => {
    return (
      <div
        className={cx(className, 'global-nav-revamp')}
      >
        {React.Children.only(this.props.children)}
      </div>
    );
  };

  handleProfileChange = attrs =>
    Actions.updateProfile(attrs, this.props.profile);
}



// WEBPACK FOOTER //
// ./packages/bridge-global-nav/src/AtlasWrapper.js